<template>
    <div class="zs-error">
        <i class="fa-regular fa-circle-exclamation zs-error__icon" />
        <p class="zs-error__title">{{ title ?? 'Something went wrong' }}</p>
        <p v-if="message && message !== title" class="zs-error__sub">{{ message }}</p>
        <button v-if="retry" type="button" class="zs-btn zs-error__action" :disabled="busy" @click="onRetry">
            <i class="fa fa-refresh" :class="{ 'fa-spin': busy }" />
            <span>Try again</span>
        </button>
        <slot name="actions" />
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

/**
 * Standard load-error state (the error sibling of the `zs-empty` empty state). Use for "we couldn't load
 * this data" failures on lists/screens — NOT for inline form/validation errors (those use `.error-message`).
 * Pass `retry` (e.g. your `load` fn) to render a "Try again" button.
 */
const props = defineProps<{
    title?: string;
    message?: string;
    retry?: () => unknown | Promise<unknown>;
}>();

const busy = ref(false);

async function onRetry() {
    if (!props.retry || busy.value) return;
    busy.value = true;
    try {
        await props.retry();
    } finally {
        busy.value = false;
    }
}
</script>
